import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import dayjs from "dayjs";
import React, { FC, useEffect, useMemo, useState } from "react";
import { useForm, useWatch } from "react-hook-form";
import { useSelector } from "react-redux";
import { useGetLoggedInUserDetails } from "../../../../../../../../../../aAdminTabTeam2/hooksAndFunctions/customHooks/useGetLoggedInUserDetails";
import {
  FormController,
  MuiBox,
  MuiCheckbox,
  MuiDateField,
  MuiGrid,
  MuiSelect,
  MuiTable,
  MuiTextField,
  MuiTypography,
  RowProps,
  StatusBadge,
  TableHeaderSearchIcon,
} from "../../../../../../../../../../components";
import MuiChip from "../../../../../../../../../../components/chip/MuiChip";
import { MuiSelectedList } from "../../../../../../../../../../components/selectedList/MuiSelectedList";
import {
  DATE_FORMAT,
  EMPTY_VALUE_DASH,
  MAX_LENGTH_50,
  PAGE_IDS,
} from "../../../../../../../../../../helpers/constants";
import { getDateFormatByCountry } from "../../../../../../../../../../helpers/constants/countryDateFormat";
import {
  convertToUserFormat,
  filterListByFilterKeyValue,
  getValueOrBlank,
  isEmpty,
  isNotEmpty,
  replaceHashWithValue,
} from "../../../../../../../../../../helpers/utils";
import { useNavigationSiteCore } from "../../../../../../../../../../hooks/useNavigationSiteCore";
import { useSiteCore } from "../../../../../../../../../../hooks/useSiteCore";
import { siteCoreContentLoadingSelector } from "../../../../../../../../../../redux/slices/appState/appStateSelector";
import {
  AddExistingProgramsResultFormKey,
  AddExistingProgramsResultRow,
} from "../../../../../../../../modelsAndSchemas/Models";
import { addExistingProgramsSiteCoreKeys } from "../../../../../../../../siteCore/SiteCoreKeys";
import { AddExistingProgramSearchResultWrapper } from "./AddExistingProgramsSearchResult.style";

interface AddExistingProgramSearchResultProps {
  rows: AddExistingProgramsResultRow[];
  setSelectedPrograms: (participants: AddExistingProgramsResultRow[]) => void;
  preSelectedChildPrograms?: AddExistingProgramsResultRow[];
}

const pageId = PAGE_IDS.ADD_EXISTING_PROGRAM_SEARCH_RESULT;

export const AddExistingProgramSearchResult: FC<
  AddExistingProgramSearchResultProps
> = ({ rows, setSelectedPrograms, preSelectedChildPrograms }) => {
  const {
    useFetchLabel,
    fetchValueByKeyOfDropdown,
    fetchDropdownValues,
    commonLabels,
  } = useSiteCore();
  const { commonARIALabel } = useNavigationSiteCore();
  const siteCoreLoading = useSelector(siteCoreContentLoadingSelector);
  const { loggedInUser } = useGetLoggedInUserDetails();
  const userCountryDateFormat = getDateFormatByCountry(
    getValueOrBlank(loggedInUser?.countryCode),
  );

  const {
    addExistingProgramsTitle,
    programKeyTableColumn,
    programNameTableColumn,
    programStatusTableColumn,
    clientManagedStatusTableColumn,
    clientNameTableColumn,
    participantMobileNumberTableColumn,
    participantEmailTableColumn,
    createdDateTableColumn,
    projectManagerTableColumn,
    clientManagedStatusList,
    programResultStatusList,
    resultsSectionHeader,
    chooseProgramsHeader,
    programsSelected,
  } = addExistingProgramsSiteCoreKeys;

  const getValueByKeyOfDropdown = (listKey: string, key: string) =>
    fetchValueByKeyOfDropdown(addExistingProgramsTitle, listKey, key);
  const getDropdownValues = (listKey: string) =>
    fetchDropdownValues(addExistingProgramsTitle, listKey, true);
  const getLabel = useFetchLabel(addExistingProgramsTitle);

  const tableRef = React.useRef<{ resetPagination: () => void }>(null);
  const [filteredResults, setFilteredResults] = useState([...rows]);
  const [isAllSelected, setIsAllSelected] = useState<boolean>(false);
  const [allUpdatedRows, setAllUpdatedRows] = useState<
    AddExistingProgramsResultRow[]
  >([]);

  const { control, setValue, getValues, reset } = useForm({
    mode: "all",
    defaultValues: {
      [AddExistingProgramsResultFormKey.programName]: "",
      [AddExistingProgramsResultFormKey.keyNumber]: "",
      [AddExistingProgramsResultFormKey.programStatus]: "",
      [AddExistingProgramsResultFormKey.projectManager]: "",
      [AddExistingProgramsResultFormKey.clientManagedStatus]: "",
      [AddExistingProgramsResultFormKey.participantEmail]: "",
      [AddExistingProgramsResultFormKey.participantMobileNumber]: "",
      [AddExistingProgramsResultFormKey.createdDate]: "",
    },
  });
  const tableColumnFilterWatch = useWatch({ control });

  const resetPagination = () => {
    if (tableRef.current) {
      tableRef.current.resetPagination();
    }
  };

  useEffect(() => {
    if (isNotEmpty(allUpdatedRows)) {
      const filterCreatedDate = tableColumnFilterWatch.createdDate
        ? dayjs(
            tableColumnFilterWatch.createdDate,
            userCountryDateFormat,
          ).format(DATE_FORMAT.YYYY_MM_DD_HYPHEN)
        : "";
      const filterList = filterListByFilterKeyValue(allUpdatedRows, {
        ...tableColumnFilterWatch,
        createdDate: filterCreatedDate,
      }) as AddExistingProgramsResultRow[];
      setFilteredResults([...filterList]);
      resetPagination();
      setAllCheckboxSelected(filterList);
    }
  }, [tableColumnFilterWatch]);

  const setAllCheckboxSelected = (
    updatedRows: AddExistingProgramsResultRow[],
  ) => {
    // Check if all rows have isChecked as true
    const allSelected = updatedRows.every(
      (row: AddExistingProgramsResultRow) => row.isChecked === true,
    );
    setIsAllSelected(updatedRows.length ? allSelected : false);
  };

  useEffect(() => {
    if (!siteCoreLoading) {
      // this is to map the existing records & show checkbox as checked
      const preSelectedIds = new Set(
        preSelectedChildPrograms?.map((p) => p.id),
      );
      const mappedResults = rows.map((item) => {
        return {
          ...item,
          programStatusLabel: getValueByKeyOfDropdown(
            programResultStatusList,
            item.programStatus,
          ),
          isChecked: preSelectedIds.has(item.id),
          isDisabled: preSelectedIds.has(item.id),
        };
      });

      setAllUpdatedRows(mappedResults);
      setFilteredResults([...mappedResults]);
      setIsAllSelected(false);
      reset();
      resetPagination();
    }
  }, [rows, siteCoreLoading, preSelectedChildPrograms]);

  const updateSelectedDataToParent = (
    filteredResults: AddExistingProgramsResultRow[],
  ) => {
    // update selected participants in parent component
    setSelectedPrograms(filteredResults);
  };

  const evaluateIsChecked = (
    checked: boolean,
    isProgramInvoiced?: boolean,
    isDisabled?: boolean,
  ) => {
    let isChecked = checked;

    if (isProgramInvoiced) {
      isChecked = false;
    } else if (isDisabled) {
      isChecked = true;
    }

    return isChecked;
  };

  const onHeaderCheckboxChange = (checked: boolean) => {
    setIsAllSelected(checked);
    const updatedRowsWithCheckedStatus = filteredResults.map(
      (row: AddExistingProgramsResultRow) => ({
        ...row,
        isChecked: evaluateIsChecked(
          checked,
          row?.isProgramInvoiced,
          row?.isDisabled,
        ),
      }),
    );

    setFilteredResults([...updatedRowsWithCheckedStatus]);

    // Also update `allUpdatedRows` to reflect changes for the whole dataset
    setAllUpdatedRows([...updatedRowsWithCheckedStatus]);

    // Update parent component state
    updateSelectedDataToParent([...updatedRowsWithCheckedStatus]);
  };

  const onItemCheckboxChange = (checked: boolean, itemId: string) => {
    const updatedRows = filteredResults.map(
      (row: AddExistingProgramsResultRow) =>
        row.id === itemId
          ? {
              ...row,
              isChecked: evaluateIsChecked(
                checked,
                row?.isProgramInvoiced,
                row?.isDisabled,
              ),
            }
          : row,
    );
    setFilteredResults([...updatedRows]);
    setAllCheckboxSelected(updatedRows);
    setAllUpdatedRows([...updatedRows]);

    // Update parent component state
    updateSelectedDataToParent([...updatedRows]);
  };

  const columns = [
    {
      id: AddExistingProgramsResultFormKey.isChecked,
      label: "",
      className: "checkbox-cell",
      isSortVisible: false,
      headerRender: () => (
        <MuiCheckbox
          checked={isAllSelected}
          checkboxLabel={`${commonARIALabel?.selectAll}`}
          hideLabel={true}
          onCheckedChange={(checked: boolean) =>
            onHeaderCheckboxChange(checked)
          }
        />
      ),
      render: (row: RowProps) => {
        return (
          <MuiCheckbox
            disabled={row?.isProgramInvoiced || row?.isDisabled}
            checked={row.isChecked ?? false}
            hideLabel={true}
            checkboxLabel={replaceHashWithValue(
              commonARIALabel.selectRowItemName,
              `${row.name}`,
            )}
            id={`${pageId}-${row.id}`}
            onCheckedChange={(checked: boolean) => {
              onItemCheckboxChange(checked, row.id as string);
            }}
          />
        );
      },
    },
    {
      id: AddExistingProgramsResultFormKey.programName,
      isSortVisible: true,
      label: getLabel(programNameTableColumn),
    },
    {
      id: AddExistingProgramsResultFormKey.keyNumber,
      isSortVisible: true,
      label: getLabel(programKeyTableColumn),
    },
    {
      id: AddExistingProgramsResultFormKey.programStatus,
      isEmptyValue: true,
      label: getLabel(programStatusTableColumn),
      isSortVisible: true,
      render: (row: any) => (
        <StatusBadge
          label={row.programStatusLabel}
          status={row.programStatus}
        />
      ),
    },
    {
      id: AddExistingProgramsResultFormKey.clientManagedStatus,
      isEmptyValue: true,
      label: getLabel(clientManagedStatusTableColumn),
      isSortVisible: true,
      render: (row: RowProps) => {
        if (isEmpty(row.clientManagedStatus)) {
          return <></>;
        }
        const statusValue = getValueByKeyOfDropdown(
          clientManagedStatusList,
          row.clientManagedStatus,
        );

        return (
          <MuiChip
            isEditable={false}
            data={[
              {
                label: statusValue,
                value: statusValue,
              },
            ]}
            removeChipNameARIALabel={commonARIALabel.removeChipName}
            showMoreARIALabel={commonARIALabel.showNMore}
          />
        );
      },
    },
    {
      id: AddExistingProgramsResultFormKey.participantEmail,
      isEmptyValue: true,
      label: getLabel(participantEmailTableColumn),
      isSortVisible: true,
    },
    {
      id: AddExistingProgramsResultFormKey.participantMobileNumber,
      isEmptyValue: true,
      label: getLabel(participantMobileNumberTableColumn),
      isSortVisible: true,
    },
    {
      id: AddExistingProgramsResultFormKey.projectManager,
      isEmptyValue: true,
      label: getLabel(projectManagerTableColumn),
      isSortVisible: true,
    },
    {
      id: AddExistingProgramsResultFormKey.createdDate,
      isSortVisible: true,
      isEmptyValue: true,
      label: getLabel(createdDateTableColumn),
      render: (row: any) => (
        <MuiBox>
          <MuiTypography variant="h3" className="">
            {isNotEmpty(row.createdDate)
              ? convertToUserFormat(row.createdDate, userCountryDateFormat)
              : EMPTY_VALUE_DASH}
          </MuiTypography>
        </MuiBox>
      ),
    },
  ];

  const getDropDownValueAsPerKey = (name: string) => {
    switch (name) {
      case AddExistingProgramsResultFormKey.programStatus:
        return getDropdownValues(programResultStatusList);
      case AddExistingProgramsResultFormKey.clientManagedStatus:
        return getDropdownValues(clientManagedStatusList);
      default:
        return [];
    }
  };

  const renderFilterDropdown = (name: string) => {
    const options = getDropDownValueAsPerKey(name);
    return (
      <FormController key={name} control={control} name={name}>
        <MuiSelect
          id={`${pageId}-${name}`}
          value=""
          label=""
          options={options}
          visibleDefaultSelectOption={true}
          requiredARIALabel={commonARIALabel.required}
          placeholder={commonLabels.selectPlaceHolder}
        />
      </FormController>
    );
  };

  const renderFilterTextField = (name: string) => {
    return (
      <FormController key={name} control={control} name={name}>
        <MuiTextField
          key={name}
          id={`${pageId}-${name}`}
          aria-label={commonLabels.search}
          startAdornment={<TableHeaderSearchIcon />}
          data-testid="company-input"
          showCloseIcon={true}
          placeholder={commonLabels.search}
          maxLength={MAX_LENGTH_50}
          requiredARIALabel={commonARIALabel.required}
          shouldTrimSpaces={false}
        />
      </FormController>
    );
  };

  const tableHeaderForm = [
    <></>,
    renderFilterTextField(AddExistingProgramsResultFormKey.programName),
    renderFilterTextField(AddExistingProgramsResultFormKey.keyNumber),
    renderFilterDropdown(AddExistingProgramsResultFormKey.programStatus),
    renderFilterDropdown(AddExistingProgramsResultFormKey.clientManagedStatus),
    renderFilterTextField(AddExistingProgramsResultFormKey.participantEmail),
    renderFilterTextField(
      AddExistingProgramsResultFormKey.participantMobileNumber,
    ),
    renderFilterTextField(AddExistingProgramsResultFormKey.projectManager),
    <FormController
      control={control}
      name={AddExistingProgramsResultFormKey.createdDate}
      key={AddExistingProgramsResultFormKey.createdDate}
    >
      <LocalizationProvider dateAdapter={AdapterDayjs}>
        <MuiDateField
          name="createdDate"
          format={userCountryDateFormat}
          clearable
          onChange={(newValue) => {
            setValue(
              AddExistingProgramsResultFormKey.createdDate,
              newValue?.format(userCountryDateFormat) ?? "",
            );
          }}
          value={
            getValues(AddExistingProgramsResultFormKey.createdDate) !== ""
              ? dayjs(
                  getValues(
                    AddExistingProgramsResultFormKey.createdDate,
                  ) as string,
                  userCountryDateFormat,
                )
              : null
          }
          selectDateARIALabel={commonARIALabel.selectDate}
          requiredARIALabel={commonARIALabel.required}
        />
      </LocalizationProvider>
    </FormController>,
  ];

  const selectedItems = useMemo(() => {
    return allUpdatedRows
      .filter((item) => item.isChecked)
      .map((item) => ({
        label: getValueOrBlank(item.programName),
        value: item.id,
      }));
  }, [allUpdatedRows]);

  const handleRemove = (id: number | string) => {
    onItemCheckboxChange(false, id as string);
  };

  return (
    <AddExistingProgramSearchResultWrapper>
      <MuiGrid container>
        <MuiGrid size={{ xs: 12 }} className="form-header">
          <MuiTypography variant="h2SemiBold" className="form-header-left">
            {getLabel(resultsSectionHeader)}
          </MuiTypography>
          {rows.length > 0 && (
            <MuiTypography variant="h3SemiBold" className="selected-count">
              {rows.length}
            </MuiTypography>
          )}
        </MuiGrid>
        <MuiBox className="search-result-header-wrapper">
          <MuiTypography
            className="search-result-header"
            component="div"
            variant="h2SemiBold"
          >
            {getLabel(chooseProgramsHeader)}
          </MuiTypography>
          <MuiBox className="search-header-right">
            <MuiSelectedList
              id="programs-select"
              selectedItems={selectedItems}
              label={replaceHashWithValue(
                getLabel(programsSelected),
                String(selectedItems?.length ?? 0),
              )}
              onRemove={handleRemove}
              requiredARIALabel={commonARIALabel.required}
            />
          </MuiBox>
        </MuiBox>
      </MuiGrid>
      <MuiTable
        pageId={pageId}
        ref={tableRef}
        columns={columns}
        tableHeaderForm={!siteCoreLoading ? tableHeaderForm : []}
        noDataInApi={isEmpty(filteredResults)}
        emptyMessage={commonLabels.noResult}
        tableResults={[...filteredResults]}
        isLastColumnSticky={false}
        priorityResults={[]}
      />
    </AddExistingProgramSearchResultWrapper>
  );
};
